<template>
    <vProjectHeader
        logoColor="legal"
        :categories="categories"
        :actions="dotsMenuActions"
        :project="footer"
        :shareLinks="shareLinks"
        :hasChanges="hasChanges"
        :hasSnapshot="hasSnapshot"
        :hidePublish="isCreatorShown"
        :hideShare="isCreatorShown"
        @toggleCategory="toggleCategory($event)"
        @publish="$emit('publish')"
        @share="$emit('share')"
    >
        <template #share>
            <slot></slot>
        </template>
    </vProjectHeader>
</template>

<script>
import {
    FooterIcon,
    PaintbrushIcon,
    NewFileIcon,
    BuildingIcon,
    TextFileIcon,
    ArrowLeftIcon,
} from "@easy/ui/icons";
import { LEGAL_PREVIEW_KIND, LEGAL_ROUTE_NAMES } from "@/apps/legal/legal.routes";
import { mapState } from "vuex";
import vProjectHeader from "@/shared/views/vProjectHeader.vue";
import { storeToRefs } from "pinia";
import { useLegalCreatorStore } from "@/apps/legal/project/onboarding/legal-creator.store";
import { PAGE_ROUTE_NAMES } from "@/apps/page/page.routes";

export default {
    components: {
        vProjectHeader,
    },
    props: {
        actions: {
            type: Array,
            default: () => [],
        },
        shareLinks: {
            type: Array,
            default: () => [],
        },
    },
    emits: ["publish", "share"],
    setup() {
        const legalCreatorStore = useLegalCreatorStore();
        const { isCreatorShown, onboardState } = storeToRefs(legalCreatorStore);
        return { isCreatorShown, onboardState };
    },
    data() {
        return {
            openFooter: false,
            openPages: false,
            openDocuments: false,
        };
    },
    computed: {
        ...mapState("legal", ["footer", "hasChanges", "hasSnapshot"]),
        dotsMenuActions() {
            return this.isCreatorShown ? [] : this.actions;
        },
        categories() {
            const { page: projectUuid } = this.$route.query;
            const backToPageEdit =
                this.$route.query.page && !this.onboardState.isLoading
                    ? {
                          name: "page",
                          label: this.$t("page.menu.back"),
                          icon: ArrowLeftIcon,
                          handler: () => {
                              this.$router.push({
                                  name: PAGE_ROUTE_NAMES.projectSections,
                                  params: { projectUuid },
                              });
                          },
                      }
                    : {};
            if (this.isCreatorShown) {
                return [
                    backToPageEdit,
                    {
                        label: this.$t("shared.creator.create_new"),
                        name: "page",
                        icon: BuildingIcon,
                    },
                ];
            }
            return [
                backToPageEdit,
                {
                    label: this.$t("legal.menu.company"),
                    icon: BuildingIcon,
                    name: LEGAL_ROUTE_NAMES.projectCompany,
                    handler: () => this.routeTo(LEGAL_ROUTE_NAMES.projectCompany),
                },
                {
                    name: "pages",
                    label: this.$t("legal.menu.pages"),
                    icon: NewFileIcon,
                    isOpen: this.openPages,
                    handler: () =>
                        this.routeTo(LEGAL_ROUTE_NAMES.projectPagesTerms, LEGAL_PREVIEW_KIND.terms),
                    children: [
                        {
                            label: this.$t("legal.views.terms"),
                            name: LEGAL_ROUTE_NAMES.projectPagesTerms,
                            handler: () =>
                                this.routeTo(
                                    LEGAL_ROUTE_NAMES.projectPagesTerms,
                                    LEGAL_PREVIEW_KIND.terms
                                ),
                        },
                        {
                            label: this.$t("legal.views.privacy"),
                            name: LEGAL_ROUTE_NAMES.projectPagesPolicy,
                            handler: () =>
                                this.routeTo(
                                    LEGAL_ROUTE_NAMES.projectPagesPolicy,
                                    LEGAL_PREVIEW_KIND.privacy
                                ),
                        },
                        {
                            label: this.$t("legal.views.about"),
                            name: LEGAL_ROUTE_NAMES.projectPagesAbout,
                            handler: () =>
                                this.routeTo(
                                    LEGAL_ROUTE_NAMES.projectPagesAbout,
                                    LEGAL_PREVIEW_KIND.about
                                ),
                        },
                    ],
                },
                {
                    name: "documents",
                    label: this.$t("legal.menu.documents"),
                    icon: TextFileIcon,
                    isOpen: this.openDocuments,
                    handler: () =>
                        this.routeTo(LEGAL_ROUTE_NAMES.projectDocumentsNda, LEGAL_PREVIEW_KIND.nda),
                    children: [
                        {
                            label: this.$t("legal.views.nda"),
                            name: LEGAL_ROUTE_NAMES.projectDocumentsNda,
                            handler: () =>
                                this.routeTo(
                                    LEGAL_ROUTE_NAMES.projectDocumentsNda,
                                    LEGAL_PREVIEW_KIND.nda
                                ),
                        },
                    ],
                },
                {
                    name: "footer",
                    label: this.$t("legal.menu.footer"),
                    icon: FooterIcon,
                    isOpen: this.openFooter,
                    handler: () =>
                        this.routeTo(
                            LEGAL_ROUTE_NAMES.projectFooterContent,
                            LEGAL_PREVIEW_KIND.footer
                        ),
                    children: [
                        {
                            label: this.$t("legal.menu.layout_content"),
                            name: LEGAL_ROUTE_NAMES.projectFooterContent,
                            handler: () =>
                                this.routeTo(
                                    LEGAL_ROUTE_NAMES.projectFooterContent,
                                    LEGAL_PREVIEW_KIND.footer
                                ),
                        },
                        {
                            label: this.$t("legal.menu.navigation"),
                            name: LEGAL_ROUTE_NAMES.projectFooterNavigation,
                            handler: () =>
                                this.routeTo(
                                    LEGAL_ROUTE_NAMES.projectFooterNavigation,
                                    LEGAL_PREVIEW_KIND.footer
                                ),
                        },
                        {
                            label: this.$t("legal.menu.socials"),
                            name: LEGAL_ROUTE_NAMES.projectFooterSocials,
                            handler: () =>
                                this.routeTo(
                                    LEGAL_ROUTE_NAMES.projectFooterSocials,
                                    LEGAL_PREVIEW_KIND.footer
                                ),
                        },
                    ],
                },
                {
                    name: LEGAL_ROUTE_NAMES.projectStyles,
                    label: this.$t("legal.menu.styles"),
                    icon: PaintbrushIcon,
                    handler: () =>
                        this.routeTo(LEGAL_ROUTE_NAMES.projectStyles, LEGAL_PREVIEW_KIND.footer),
                },
            ];
        },
    },
    methods: {
        routeTo(name, forcedView) {
            const { query } = this.$route;
            this.$router.push({ name, query: forcedView ? { ...query, view: forcedView } : query });
        },
        //FIXME: This is problem of passing computed categories
        toggleCategory(category) {
            if (category.name === "footer") {
                this.openFooter = !this.openFooter;
            }
            if (category.name === "pages") {
                this.openPages = !this.openPages;
            }
            if (category.name === "documents") {
                this.openDocuments = !this.openDocuments;
            }
        },
    },
};
</script>
